import React from 'react';
import { 
  TrendingUp, 
  Users,
  ExternalLink
} from 'lucide-react';
import { formatCompactCurrency, formatDate } from '@/utils';

interface ProjectFundingRound {
  round_name: string;
  date: string;
  fully_diluted_valuation: number;
  amount_raised: number;
  investors: string[];
}

interface FundingRoundDropdownProps {
  round: ProjectFundingRound;
  index: number;
}

const FundingRoundDropdown: React.FC<FundingRoundDropdownProps> = ({ round, index }) => {
  // Only show amount raised if it's greater than 0
  const showAmountRaised = (round.amount_raised || 0) > 0;
  const showValuation = (round.fully_diluted_valuation || 0) > 0;
  // Only show investors if there are any
  const showInvestors = round.investors && round.investors.length > 0;

  const amountRaised = formatCompactCurrency(round.amount_raised || 0);
  const formattedValuation = showValuation ? formatCompactCurrency(round.fully_diluted_valuation || 0) : null;

  const renderCurrencyValue = (value: string) => {
    const trimmedValue = value.trim();
    if (!trimmedValue.startsWith('$')) {
      return trimmedValue;
    }

    return (
      <>
        <span className="text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">$</span>{' '}
        {trimmedValue.slice(1)}
      </>
    );
  };

  return (
    <div className="bg-white dark:bg-[#1c1c1c] border border-[rgba(45,41,35,0.12)] px-5 py-4">
      {/* Header Section */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-4">
          {/* Icon container */}
          <div className="w-12 h-12 bg-[#e3fd5a] flex items-center justify-center flex-shrink-0">
            <TrendingUp size={24} className="text-[#2d2923]" />
          </div>
          {/* Round info */}
          <div className="flex flex-col gap-[6px]">
            <h3 className="font-['Plus_Jakarta_Sans'] font-semibold text-[16px] leading-[24px] text-[#2d2923] dark:text-[#fdfdfd]">
              {round.round_name || `Round ${index + 1}`}
            </h3>
            <p className="font-['Plus_Jakarta_Sans'] font-medium text-[13px] leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
              {formatDate(round.date)}
            </p>
          </div>
        </div>
        
        {/* Amount raised */}
        {showAmountRaised && (
          <div className="text-right flex flex-col items-end gap-[6px]">
            <p className="font-['Plus_Jakarta_Sans'] font-medium text-[18px] leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
              {renderCurrencyValue(amountRaised)}
            </p>
            {formattedValuation && (
              <p className="font-['Plus_Jakarta_Sans'] font-normal text-[12px] leading-[16px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                Valuation: {formattedValuation}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Investors Section */}
      {showInvestors && round.investors && (
        <>
          {/* Divider */}
          <div className="border-t border-[rgba(45,41,35,0.12)] mb-4" />
          
          {/* Total Investors header */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-[6px]">
              <Users size={16} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />
              <span className="font-['Plus_Jakarta_Sans'] font-medium text-[13px] leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                Total Investors
              </span>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={round.investors.length}
                readOnly
                className="w-12 h-7 border border-[rgba(45,41,35,0.12)] dark:border-[rgba(253,253,253,0.12)] bg-white dark:bg-[#1c1c1c] px-2 text-center font-['Plus_Jakarta_Sans'] font-medium text-[13px] leading-[18px] text-[#2d2923] dark:text-[#fdfdfd] focus:outline-none focus:ring-0"
              />
            </div>
          </div>
          
          {/* Publicly available notice */}
          <p className="font-['Plus_Jakarta_Sans'] font-normal text-[12px] leading-[16px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)] mb-3">
            Publicly available via media
          </p>
          
          {/* Investor badges */}
          <div className="flex flex-wrap gap-2">
            {round.investors.map((investor: string, investorIndex: number) => (
              <div
                key={investorIndex}
                className="flex items-center gap-[6px] bg-[rgba(45,41,35,0.04)] dark:bg-[rgba(253,253,253,0.04)] border border-[rgba(45,41,35,0.12)] dark:border-[rgba(253,253,253,0.12)] pl-3 pr-4 py-[6px]"
              >
                <ExternalLink size={10} className="text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]" />
                <span className="font-['Plus_Jakarta_Sans'] font-medium text-[13px] leading-[18px] text-[#2d2923] dark:text-[#fdfdfd]">
                  {investor}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default FundingRoundDropdown;
